import React from 'react';
import { motion } from 'framer-motion';

// Moodboard — renders a 2/3/4-column grid of cinematic plates.
//
// Each tile may carry either:
//   • `image` — a URL to a real moodboard still (preferred for production)
//   • `gradient` — a painted CSS gradient (used as a stand-in placeholder)
//
// If both are present, the image wins. We still apply the grain + scanline
// overlays on top of the image so the entire grid keeps a unified, slightly
// "treated" cinematic look (no plate ever feels like a raw stock photo).
export default function Moodboard({ tiles = [] }) {
  return (
    <div
      data-testid="moodboard-grid"
      className="mt-10 grid grid-cols-1 xl:grid-cols-2 gap-6"
    >
      {tiles.map((t, i) => (
        <motion.div
          key={t.id}
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.55, delay: i * 0.05, ease: 'easeOut' }}
          className="relative aspect-[2.39/1] rounded-xl overflow-hidden gold-border group bg-black"
          data-testid={`moodboard-tile-${i + 1}`}
        >
          {/* Real moodboard still if provided, otherwise a painted gradient.
              We use object-contain so the FULL cinematic frame is preserved —
              no cropping of action, faces, or composition — sitting on a true
              black backdrop that blends with the source's letterbox bars. */}
          {t.image ? (
            <img
              src={t.image}
              alt={t.label}
              loading="lazy"
              decoding="async"
              // Optional horizontal flip — applied when the tile data carries
              // `flip: true`. The hover scale composes with the flip via
              // signed scaleX, so the still mirror-flips AND still gets the
              // gentle zoom-on-hover effect.
              style={t.flip ? { transform: 'scaleX(-1)' } : undefined}
              className={`absolute inset-0 w-full h-full object-contain transition-transform duration-700 ease-out ${
                t.flip ? 'group-hover:[transform:scaleX(-1)_scale(1.02)]' : 'group-hover:scale-[1.02]'
              }`}
            />
          ) : (
            <div className="absolute inset-0" style={{ background: t.gradient }} />
          )}
          {/* Soft bottom gradient so the caption reads cleanly on bright stills.
              Kept thin so it doesn't darken the actual image. */}
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/85 via-black/30 to-transparent pointer-events-none" />
          {/* Grain + scan overlay for cinematic feel */}
          <div className="absolute inset-0 bg-noise opacity-[0.04] mix-blend-overlay pointer-events-none" />
          <div className="absolute inset-0 scanlines opacity-10 mix-blend-screen pointer-events-none" />
          {/* Caption */}
          <div className="absolute inset-x-0 bottom-0 flex items-end justify-between px-5 py-4">
            <div>
              <div className="font-ui text-[10px] tracking-[0.4em] uppercase text-[color:var(--gold)]">PLATE {String(i + 1).padStart(2, '0')}</div>
              <div className="mt-1 font-display text-[15px] sm:text-[17px] leading-tight text-[color:var(--text-cream)]">{t.label}</div>
            </div>
            <div className="font-ui text-[9px] tracking-[0.4em] uppercase text-[color:var(--text-dim)] hidden sm:block">2.39 : 1</div>
          </div>
          {/* Corner accents */}
          <span className="absolute top-3 left-3 w-4 h-4 border-l border-t border-[color:var(--gold)]/70" />
          <span className="absolute top-3 right-3 w-4 h-4 border-r border-t border-[color:var(--gold)]/70" />
          <span className="absolute bottom-3 left-3 w-4 h-4 border-l border-b border-[color:var(--gold)]/70" />
          <span className="absolute bottom-3 right-3 w-4 h-4 border-r border-b border-[color:var(--gold)]/70" />
        </motion.div>
      ))}
    </div>
  );
}
